import * as React from 'react';
import { useCallback } from 'react';

import gql from 'graphql-tag';
import _ from 'lodash';

import connectToRouter from 'js/lib/connectToRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Typography2 } from '@coursera/cds-core';

import withNotFound from 'bundles/common/components/withNotFound';
import { itemsPerPage } from 'bundles/directory-graphql/common/constants';
import { getPartnerNames } from 'bundles/directory-graphql/common/utils';
import LinksContainer from 'bundles/directory-graphql/components/LinksContainer';
import Metatags from 'bundles/directory-graphql/components/MetatagsWithPagination';
import NotFound from 'bundles/directory-graphql/components/NotFound';
import usePaginationData from 'bundles/directory-graphql/hooks/usePaginationData';
import PaginationControls from 'bundles/page/components/cds/PaginationControls';

import _t from 'i18n!nls/directory-graphql';

type DirectoryCoursePartner = {
  __typename: string;
  name: string;
};

type DirectoryCourse = {
  __typename: string;
  id: string;
  name: string;
  courseStatus: string;
  partners: {
    __typename: string;
    elements: Array<DirectoryCoursePartner>;
  };
  slug: string;
};

type Props = {
  courses: Array<DirectoryCourse>;
  total: number;
  linkedToReviewPages: boolean;
};

export const Courses = ({ linkedToReviewPages, total, courses }: Props) => {
  const { currentPage, pageCount } = usePaginationData(total, itemsPerPage);

  const titledLinks = _.sortBy(
    _.map(courses, ({ name, partners, slug }) => {
      const partnerNames = getPartnerNames(partners);
      const courseLink = `/learn/${slug}`;
      return {
        title: `${name.trim()} (${partnerNames})`,
        link: linkedToReviewPages ? `/learn/${slug}/reviews` : courseLink,
      };
    }),
    'title'
  );

  const getMetatagsAdditionalSettings = useCallback<() => $TSFixMe>(() => {
    let additionalSettings = {};
    if (linkedToReviewPages) {
      // adjusts metatags for the course reviews directory
      additionalSettings = {
        title: _t('Course Reviews Directory'),
        description: _t(
          'The Coursera Reviews directory includes links to all of our courses with learner reviews and ratings.'
        ),
        disableCrawlerIndexing: true,
        useFollowForDisablingIndexing: true,
      };
    } else {
      // sets metatags for the courses directory
      additionalSettings = {
        title: _t('Courses Directory'),
        description: _t(
          'The Coursera Courses directory includes links to all of our #{total} Course description pages.',
          { total }
        ),
        disableCrawlerIndexing: true,
        useFollowForDisablingIndexing: true,
      };
    }

    return additionalSettings;
  }, [linkedToReviewPages, total]);

  if (!titledLinks) {
    return null;
  } else {
    return (
      <div>
        <Typography2 component="p" variant="bodyPrimary">
          {linkedToReviewPages && _t('Please select a course to see its reviews.')}
        </Typography2>
        <LinksContainer titledLinks={titledLinks} />
        <PaginationControls
          currentPage={currentPage}
          largeStyle
          pageCount={pageCount}
          maxPages={4}
          renderLinks={true}
        />
        <Metatags total={total} additionalSettings={getMetatagsAdditionalSettings()} />
      </div>
    );
  }
};

export default _.flowRight(
  connectToRouter((router) => ({ page: router.location.query.page })),
  waitForGraphQL(
    gql`
      query DirectoryPageCoursesQuery($start: String!, $limit: Int!) {
        CoursesV1Resource {
          getAll(sortedBy: "name", start: $start, limit: $limit) {
            courses: elements {
              id
              name
              slug
              courseStatus
              partners {
                elements {
                  name
                }
              }
            }
            paging {
              total
            }
          }
        }
      }
    `,
    {
      props: ({
        data: {
          // @ts-expect-error TSMIGRATION
          CoursesV1Resource: {
            getAll: {
              courses,
              paging: { total },
            },
          },
        },
      }) => ({
        courses,
        total,
      }),
      // @ts-expect-error TSMIGRATION
      options({ page = '1' }) {
        const start = (parseInt(page, 10) - 1) * itemsPerPage;
        return {
          variables: {
            start: start.toString(),
            limit: itemsPerPage,
          },
        };
      },
    }
  ),
  // if page is 0 or negative, or more than the total # of pages, return 404
  withNotFound({
    test: ({ page = '1', total }) => {
      const pageInt = parseInt(page, 10);
      return _.isNaN(pageInt) || pageInt < 1 || pageInt > Math.ceil(total / itemsPerPage);
    },
    errorComponent: NotFound,
  })
)(Courses);
